/**
 * LeaveApprovalRuleService
 * Admin setup of who gives line approval: department rules (head of department or named people) and
 * individual rules (named people), plus the chain preview, a health report and the lookups.
 * Route: api/leave-approval-rules (HRAdmin)
 *
 * Every create, update and deactivate re-resolves the pending leaves before it responds.
 * Refusals come back as HTTP 400 (validation), 404 or 409 (the department or person already has an
 * active rule) with a ready-to-show message (thrown by the HttpClient as a CCMSError).
 */
import { BaseService } from '../base/BaseService';
export class LeaveApprovalRuleService extends BaseService {
    constructor(http, config) {
        const internalConfig = {
            servicePath: config?.servicePath ?? LeaveApprovalRuleService.DEFAULT_SERVICE_PATH,
            basePath: LeaveApprovalRuleService.BASE_PATH,
            endpoints: config?.endpoints,
        };
        super(http, internalConfig);
    }
    // ==================== RULES ====================
    /**
     * List rules, optionally by scope (1 Department, 2 Individual) and including switched-off rules.
     * GET api/leave-approval-rules?scopeId=&includeInactive=false
     */
    async getRules(options) {
        const response = await this.getRulesRaw(options);
        return response.data ?? [];
    }
    getRulesRaw(options) {
        const endpoint = this.getEndpoint('list', '');
        return this.http.get(this.buildUrlWithParams(endpoint, {
            scopeId: options?.scopeId,
            includeInactive: options?.includeInactive,
        }));
    }
    /**
     * One rule (404 if missing).
     * GET api/leave-approval-rules/{ruleId}
     */
    async getRule(ruleId) {
        const response = await this.getRuleRaw(ruleId);
        if (!response.data) {
            throw new Error(`Approval rule ${ruleId} not found`);
        }
        return response.data;
    }
    getRuleRaw(ruleId) {
        const endpoint = this.getEndpoint('get', `${ruleId}`);
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Create a rule (201). 409 if the department or person already has an active rule; 400 on validation.
     * POST api/leave-approval-rules
     */
    async createRule(request) {
        const response = await this.createRuleRaw(request);
        if (!response.data) {
            throw new Error('Failed to create the approval rule');
        }
        return response.data;
    }
    createRuleRaw(request) {
        const endpoint = this.getEndpoint('create', '');
        return this.http.post(this.buildUrl(endpoint), request);
    }
    /**
     * Update a rule.
     * PUT api/leave-approval-rules/{ruleId}
     */
    async updateRule(ruleId, request) {
        const response = await this.updateRuleRaw(ruleId, request);
        if (!response.data) {
            throw new Error(`Failed to update approval rule ${ruleId}`);
        }
        return response.data;
    }
    updateRuleRaw(ruleId, request) {
        const endpoint = this.getEndpoint('update', `${ruleId}`);
        return this.http.put(this.buildUrl(endpoint), request);
    }
    /**
     * Switch a rule off (it is kept, with isActive=false). Returns the deactivated rule.
     * DELETE api/leave-approval-rules/{ruleId}
     */
    async deactivateRule(ruleId) {
        const response = await this.deactivateRuleRaw(ruleId);
        if (!response.data) {
            throw new Error(`Failed to switch off approval rule ${ruleId}`);
        }
        return response.data;
    }
    deactivateRuleRaw(ruleId) {
        const endpoint = this.getEndpoint('deactivate', `${ruleId}`);
        return this.http.delete(this.buildUrl(endpoint));
    }
    // ==================== PREVIEW / HEALTH ====================
    /**
     * Where a new leave for this person would go (line approvers, the rule used, final approvers, summary).
     * GET api/leave-approval-rules/preview/{userId}
     */
    async previewChain(userId) {
        const response = await this.previewChainRaw(userId);
        if (!response.data) {
            throw new Error('Failed to preview the approval chain');
        }
        return response.data;
    }
    previewChainRaw(userId) {
        const endpoint = this.getEndpoint('preview', `preview/${encodeURIComponent(userId)}`);
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * People with no line approver, head rules on departments with no head, and the HR admin situation.
     * GET api/leave-approval-rules/health
     */
    async getHealth() {
        const response = await this.getHealthRaw();
        if (!response.data) {
            throw new Error('Failed to check the approval rules');
        }
        return response.data;
    }
    getHealthRaw() {
        const endpoint = this.getEndpoint('health', 'health');
        return this.http.get(this.buildUrl(endpoint));
    }
    // ==================== PICKERS / LOOKUPS ====================
    /**
     * Department picker with each department's heads and its active rule.
     * GET api/leave-approval-rules/departments
     */
    async getDepartments() {
        const response = await this.getDepartmentsRaw();
        return response.data ?? [];
    }
    getDepartmentsRaw() {
        const endpoint = this.getEndpoint('departments', 'departments');
        return this.http.get(this.buildUrl(endpoint));
    }
    /**
     * Scopes, sources, step kinds, step statuses, ask types, ask statuses and step reasons.
     * GET api/leave-approval-rules/lookups
     */
    async getLookups() {
        const response = await this.getLookupsRaw();
        if (!response.data) {
            throw new Error('Failed to read the approval lookups');
        }
        return response.data;
    }
    getLookupsRaw() {
        const endpoint = this.getEndpoint('lookups', 'lookups');
        return this.http.get(this.buildUrl(endpoint));
    }
    // ==================== RECONCILE ====================
    /**
     * Re-resolve every pending leave now (runs the reconciler).
     * POST api/leave-approval-rules/reresolve
     */
    async reresolve() {
        const response = await this.reresolveRaw();
        if (!response.data) {
            throw new Error('Failed to re-resolve pending leaves');
        }
        return response.data;
    }
    reresolveRaw() {
        const endpoint = this.getEndpoint('reresolve', 'reresolve');
        return this.http.post(this.buildUrl(endpoint), {});
    }
}
LeaveApprovalRuleService.DEFAULT_SERVICE_PATH = 'hr';
LeaveApprovalRuleService.BASE_PATH = 'api/leave-approval-rules';
//# sourceMappingURL=LeaveApprovalRuleService.js.map